import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Container} from "@pg-design/grid-module";
import {elevation} from "@pg-design/helpers-css-strings";
import {BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {ViewType} from "../../view_type/ViewType";
import {useGetVendorDetailOfferListMapQuery} from "../api/get_vendor_detail_offer_list_map";
import {useGetVendorDetailOffersQuery} from "../api/get_vendor_detail_offers";
import {AnniversaryAwards} from "../components/detail/AnniversaryAwards";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailNavBar} from "../components/detail/VendorDetailNavBar";
import {VendorDetailOfferRanking} from "../components/detail/VendorDetailOfferRanking";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorDetailSubHeader} from "../components/detail/VendorDetailSubHeader";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {setActiveOffersCount} from "../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../utils/convert_to_vendor_detail_view_offers_payload";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

export const VendorDetailView = () => {
    const vendorDetail = useAppSelector((state) => state.vendor.detail);
    const variables = useAppSelector((state) => state.variables);
    const articles = useAppSelector((state) => state.vendor.detail.articles ?? []);
    const rankings = useAppSelector((state) => state.vendor.detail.rankings ?? []);
    const favouriteOffers = useAppSelector((state) => state.favourite.favourites.offers);
    const appliedOffers = useAppSelector((state) => state.offerListMap.appliedOffers.data);
    const region = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const dispatch = useAppDispatch();

    const {vendor} = vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const fullQueryActiveOffers = {
        ...convertToVendorDetailViewOffersPayload({vendorId: vendor?.id ?? 0, region, displayType: OfferDisplayType.FOR_SALE}),
        page: 1
    };
    const {data: activeOffers} = useGetVendorDetailOffersQuery(fullQueryActiveOffers);

    if (!region) {
        dispatch(setActiveOffersCount(activeOffers?.count ?? 0));
    }
    const {data: activeFlatsAndHouses} = useGetVendorDetailOfferListMapQuery({vendor: vendor?.id ?? 0});

    const activeFlats = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer)) ?? [];
    const activeHouses = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer)) ?? [];
    const activeCommercials = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer)) ?? [];

    const navBarRef = useRef<HTMLDivElement>(null);
    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refAwards = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refAboutUs, refAwards, refPromotions, refOffers, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const hasPromotions = !!vendor && vendor.promotions?.length > 0;
    const hasAwards = rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses ? activeFlatsAndHouses.length > 0 : false;

    const getElems = useCallback(() => {
        const res = [];

        res.push("about_us");

        if (hasAwards) {
            res.push("awards");
        }

        if (hasPromotions) {
            res.push("promotions");
        }

        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }

        res.push("contact");

        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "awards":
                    return hasAwards;
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;

            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    const isSticky = useStickyIntersection(navBarRef);

    if (!vendor) {
        return (
            <div className={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <Container>
                    <VendorBreadcrumbs />
                </Container>

                <VendorContainer>
                    <div className={css({pt: 4, pb: 2})}>
                        <VendorDetailHeader vendor={vendor} />
                    </div>
                </VendorContainer>

                <div className={cx(navigationWrapper, isSticky ? navigationWrapperSticky : navigationWrapperNonSticky)} ref={navBarRef}>
                    <VendorContainer>
                        <VendorDetailNavBar
                            refElements={refElems}
                            visibleElement={visibleElem}
                            hasPromotions={hasPromotions}
                            hasActiveFlatsAndHouses={hasActiveFlatsAndHouses}
                            hasAwards={hasAwards}
                        />
                    </VendorContainer>
                </div>

                <VendorContainer>
                    <section id="o-nas" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    {hasActiveFlatsAndHouses && activeOffers && (
                        <section id="oferta" className={activeOffersSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <div className={activeOffersHeaderHolder}>
                                <VendorDetailSubHeader vendor={vendor} className={cx(subHeaderStyle, activeOfferHeaderStyle)} />

                                <Button
                                    className={triggerButton}
                                    iconLeft={BrandMapPinIcon}
                                    variant="brand_icon"
                                    size="big"
                                    onClick={() => setIsMapModalOpen(true)}
                                >
                                    Zobacz na mapie
                                </Button>
                            </div>

                            <VendorDetailActiveOffers
                                offersInRegionRequest={vendorDetail.offersInRegionRequest}
                                vendor={vendor}
                                cities={vendorDetail.cities ?? []}
                                openMapBtnClick={() => setIsMapModalOpen(true)}
                            />
                        </section>
                    )}

                    {hasAwards && (
                        <section id="nagrody" className={awardsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refAwards} />

                            <VendorDetailOfferRanking vendor={vendor} rankings={rankings} />

                            {vendorAward && <AnniversaryAwards vendorName={vendor.name} vendorId={vendor.id} />}
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" className={promotionsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={vendor.promotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {activeCommercials.length > 0 && (
                        <section className={commercialsSectionStyle}>
                            <VendorDetailCommercials offers={activeCommercials} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <section className={soldOffersSectionStyle}>
                        <VendorDetailSoldOffers vendorSlug={vendor.slug} vendorDetail={vendor} />
                    </section>

                    <section id="kontakt" className={contactSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>

            {articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses ?? []}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={favouriteOffers}
                appliedOffers={appliedOffers}
            />
        </article>
    );
};

const loaderWrapper = css({
    borderRadius: 1,
    height: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const subHeaderStyle = css({
    flex: "0 0 100%",

    md: {
        alignSelf: "center",
        flex: "1 1 50%"
    }
});

const triggerButton = css({
    md: {
        flex: "0 0 auto",
        order: 2
    }
});

const navigationWrapperSticky = css({
    background: "var(--colors-gray-200)",
    boxShadow: elevation(4)
});

const navigationWrapperNonSticky = css({
    background: "var(--colors-gray-100)"
});

const navigationWrapper = css({
    display: "none",
    md: {
        display: "block",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        py: 2
    }
});

const generalSectionStyle = css({
    mb: 5,
    md: {
        mt: 6,
        mb: 7
    }
});

const awardsSectionStyle = css({
    mb: 8,
    md: {
        mb: 10
    }
});

const promotionsSectionStyle = css({
    mb: 5,
    md: {
        mb: 10
    }
});

const activeOffersHeaderHolder = css({
    textAlign: "center",
    mb: 8,
    md: {
        display: "flex",
        textAlign: "left",
        mb: 1.5
    }
});

const activeOfferHeaderStyle = css({
    mb: 3,

    md: {
        mb: 0
    }
});

const activeOffersSectionStyle = css({
    mb: 5,

    md: {
        mb: 8
    }
});

const commercialsSectionStyle = css({
    mb: 6,

    md: {
        mb: 8
    }
});

const soldOffersSectionStyle = css({
    mb: 6,

    md: {
        mb: 8
    }
});

const contactSectionStyle = css({
    mb: 6,

    md: {
        mb: 5
    }
});
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Container} from "@pg-design/grid-module";
import {elevation} from "@pg-design/helpers-css-strings";
import {BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {useActiveElementId, useChangeVisibility} from "@pg-mono/hooks";

import {ScrollRefOffset} from "../../atoms/ScrollRefOffset";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {OfferListMapModal} from "../../offer/list/components/OfferListMapModal/OfferListMapModal";
import {OfferType} from "../../offer/types/OfferType";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {vendorDetailPageview} from "../../tracking/algolytics_hits/vendor_detail_pageview";
import {ViewType} from "../../view_type/ViewType";
import {useGetVendorDetailOfferListMapQuery} from "../api/get_vendor_detail_offer_list_map";
import {useGetVendorDetailOffersQuery} from "../api/get_vendor_detail_offers";
import {AnniversaryAwards} from "../components/detail/AnniversaryAwards";
import {VendorDetailActiveOffers} from "../components/detail/VendorDetailActiveOffers";
import {VendorDetailArticles} from "../components/detail/VendorDetailArticles";
import {VendorDetailCommercials} from "../components/detail/VendorDetailCommercials";
import {VendorDetailContact} from "../components/detail/VendorDetailContact";
import {VendorDetailDescription} from "../components/detail/VendorDetailDescription";
import {VendorDetailHeader} from "../components/detail/VendorDetailHeader";
import {VendorDetailNavBar} from "../components/detail/VendorDetailNavBar";
import {VendorDetailOfferRanking} from "../components/detail/VendorDetailOfferRanking";
import {VendorDetailPromotions} from "../components/detail/VendorDetailPromotions";
import {VendorDetailSoldOffers} from "../components/detail/VendorDetailSoldOffers";
import {VendorDetailSubHeader} from "../components/detail/VendorDetailSubHeader";
import {VendorBreadcrumbs} from "../components/VendorBreadcrumbs";
import {VendorContainer} from "../components/VendorContainer";
import {doesVendorHaveAnniversaryAward} from "../helpers/does_vendor_have_anniversary_award";
import {setActiveOffersCount} from "../state/vendor_offer_list_slice";
import {convertToVendorDetailViewOffersPayload} from "../utils/convert_to_vendor_detail_view_offers_payload";
import {isActiveVendorOffer} from "../utils/is_active_vendor_offer";

export const VendorDetailView = () => {
    const vendorDetail = useAppSelector((state) => state.vendor.detail);
    const variables = useAppSelector((state) => state.variables);
    const articles = useAppSelector((state) => state.vendor.detail.articles ?? []);
    const rankings = useAppSelector((state) => state.vendor.detail.rankings ?? []);
    const favouriteOffers = useAppSelector((state) => state.favourite.favourites.offers);
    const appliedOffers = useAppSelector((state) => state.offerListMap.appliedOffers.data);
    const region = useAppSelector((state) => state.vendorDetailViewSlice.activeOffersListParams.region);
    const dispatch = useAppDispatch();

    const {vendor} = vendorDetail;

    useEffect(() => {
        vendor && vendorDetailPageview(vendor);
    }, [vendor]);

    const fullQueryActiveOffers = {
        ...convertToVendorDetailViewOffersPayload({vendorId: vendor?.id ?? 0, region, displayType: OfferDisplayType.FOR_SALE}),
        page: 1
    };
    const {data: activeOffers} = useGetVendorDetailOffersQuery(fullQueryActiveOffers);

    if (!region) {
        dispatch(setActiveOffersCount(activeOffers?.count ?? 0));
    }
    const {data: activeFlatsAndHouses} = useGetVendorDetailOfferListMapQuery({vendor: vendor?.id ?? 0});

    const activeFlats = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.FLAT && isActiveVendorOffer(offer)) ?? [];
    const activeHouses = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.HOUSE && isActiveVendorOffer(offer)) ?? [];
    const activeCommercials = activeFlatsAndHouses?.filter((offer) => offer.type === OfferType.COMMERCIAL && isActiveVendorOffer(offer)) ?? [];

    const navBarRef = useRef<HTMLDivElement>(null);
    const refAboutUs = useRef<HTMLTableSectionElement>(null);
    const refAwards = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refOffers = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(() => ({refAboutUs, refAwards, refPromotions, refOffers, refContact}), []);

    const vendorAward = !!vendor && doesVendorHaveAnniversaryAward(vendor);

    const hasPromotions = !!vendor && vendor.promotions?.length > 0;
    const hasAwards = rankings.length > 0 || vendorAward;
    const hasActiveFlatsAndHouses = activeFlatsAndHouses ? activeFlatsAndHouses.length > 0 : false;

    const getElems = useCallback(() => {
        const res = [];

        res.push("about_us");

        if (hasAwards) {
            res.push("awards");
        }

        if (hasPromotions) {
            res.push("promotions");
        }

        if (hasActiveFlatsAndHouses) {
            res.push("offers");
        }

        res.push("contact");

        return res;
    }, [hasAwards, hasPromotions, hasActiveFlatsAndHouses]);

    const [visibleElem, changeVisibility, viewportHeight] = useChangeVisibility(getElems, 100);

    const ID_MAPPING: Record<string, string> = {
        "o-nas": "about_us",
        nagrody: "awards",
        "oferty-specjalne": "promotions",
        oferta: "offers",
        kontakt: "contact"
    };

    const activeId = useActiveElementId(
        Object.keys(ID_MAPPING).filter((id) => {
            switch (ID_MAPPING[id]) {
                case "awards":
                    return hasAwards;
                case "promotions":
                    return hasPromotions;
                case "offers":
                    return hasActiveFlatsAndHouses;
                default:
                    return true;
            }
        }),
        {
            threshold: 0,
            rootMargin: `0px 0px -${viewportHeight}px 0px`
        }
    );

    useEffect(() => {
        if (activeId) {
            const mappedId = ID_MAPPING[activeId] || activeId;

            changeVisibility(mappedId)(true);
        } else {
            changeVisibility(visibleElem || "about_us")(false);
        }
    }, [activeId, changeVisibility, visibleElem]);

    // Offer map modal
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    const isSticky = useStickyIntersection(navBarRef);

    if (!vendor) {
        return (
            <div className={loaderWrapper}>
                <Loader />
            </div>
        );
    }

    return (
        <article>
            <div>
                <Container>
                    <VendorBreadcrumbs />
                </Container>

                <VendorContainer>
                    <div className={css({pt: 4, pb: 2})}>
                        <VendorDetailHeader vendor={vendor} />
                    </div>
                </VendorContainer>

                <div className={cx(navigationWrapper, isSticky ? navigationWrapperSticky : navigationWrapperNonSticky)} ref={navBarRef}>
                    <VendorContainer>
                        <VendorDetailNavBar
                            refElements={refElems}
                            visibleElement={visibleElem}
                            hasPromotions={hasPromotions}
                            hasActiveFlatsAndHouses={hasActiveFlatsAndHouses}
                            hasAwards={hasAwards}
                        />
                    </VendorContainer>
                </div>

                <VendorContainer>
                    <section id="o-nas" className={generalSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refAboutUs} />

                        <VendorDetailDescription
                            variables={variables}
                            vendor={vendor}
                            flatsOffers={activeFlats}
                            housesOffers={activeHouses}
                            commercialsOffers={activeCommercials}
                        />
                    </section>

                    {hasActiveFlatsAndHouses && activeOffers && (
                        <section id="oferta" className={activeOffersSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refOffers} />

                            <div className={activeOffersHeaderHolder}>
                                <VendorDetailSubHeader vendor={vendor} className={cx(subHeaderStyle, activeOfferHeaderStyle)} />

                                <Button
                                    className={triggerButton}
                                    iconLeft={BrandMapPinIcon}
                                    variant="brand_icon"
                                    size="big"
                                    onClick={() => setIsMapModalOpen(true)}
                                >
                                    Zobacz na mapie
                                </Button>
                            </div>

                            <VendorDetailActiveOffers
                                offersInRegionRequest={vendorDetail.offersInRegionRequest}
                                vendor={vendor}
                                cities={vendorDetail.cities ?? []}
                                openMapBtnClick={() => setIsMapModalOpen(true)}
                            />
                        </section>
                    )}

                    {hasAwards && (
                        <section id="nagrody" className={awardsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refAwards} />

                            <VendorDetailOfferRanking vendor={vendor} rankings={rankings} />

                            {vendorAward && <AnniversaryAwards vendorName={vendor.name} vendorId={vendor.id} />}
                        </section>
                    )}

                    {hasPromotions && (
                        <section id="oferty-specjalne" className={promotionsSectionStyle}>
                            <ScrollRefOffset offsetTop={-100} ref={refElems.refPromotions} />

                            <VendorDetailPromotions promotions={vendor.promotions} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    {activeCommercials.length > 0 && (
                        <section className={commercialsSectionStyle}>
                            <VendorDetailCommercials offers={activeCommercials} vendorSlug={vendor.slug} />
                        </section>
                    )}

                    <section className={soldOffersSectionStyle}>
                        <VendorDetailSoldOffers vendorSlug={vendor.slug} vendorDetail={vendor} />
                    </section>

                    <section id="kontakt" className={contactSectionStyle}>
                        <ScrollRefOffset offsetTop={-100} ref={refElems.refContact} />

                        <VendorDetailContact vendor={vendor} hasActiveOffers={hasActiveFlatsAndHouses} />
                    </section>
                </VendorContainer>
            </div>

            {articles.length > 0 && (
                <section>
                    <VendorDetailArticles vendorName={vendor.name} articles={articles} />
                </section>
            )}

            <LeadModal />

            <OfferListMapModal
                viewType={ViewType.VENDOR}
                offerList={activeFlatsAndHouses ?? []}
                isModalOpen={isMapModalOpen}
                onModalClose={() => setIsMapModalOpen(false)}
                favouriteOffers={favouriteOffers}
                appliedOffers={appliedOffers}
            />
        </article>
    );
};

const loaderWrapper = css({
    borderRadius: 1,
    height: 400,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const subHeaderStyle = css({
    flex: "0 0 100%",

    md: {
        alignSelf: "center",
        flex: "1 1 50%"
    }
});

const triggerButton = css({
    md: {
        flex: "0 0 auto",
        order: 2
    }
});

const navigationWrapperSticky = css({
    background: "var(--colors-gray-200)",
    boxShadow: elevation(4)
});

const navigationWrapperNonSticky = css({
    background: "var(--colors-gray-100)"
});

const navigationWrapper = css({
    display: "none",
    md: {
        display: "block",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        py: 2
    }
});

const generalSectionStyle = css({
    mb: 5,
    md: {
        mt: 6,
        mb: 7
    }
});

const awardsSectionStyle = css({
    mb: 8,
    md: {
        mb: 10
    }
});

const promotionsSectionStyle = css({
    mb: 5,
    md: {
        mb: 10
    }
});

const activeOffersHeaderHolder = css({
    textAlign: "center",
    mb: 8,
    md: {
        display: "flex",
        textAlign: "left",
        mb: 1.5
    }
});

const activeOfferHeaderStyle = css({
    mb: 3,

    md: {
        mb: 0
    }
});

const activeOffersSectionStyle = css({
    mb: 5,

    md: {
        mb: 8
    }
});

const commercialsSectionStyle = css({
    mb: 6,

    md: {
        mb: 8
    }
});

const soldOffersSectionStyle = css({
    mb: 6,

    md: {
        mb: 8
    }
});

const contactSectionStyle = css({
    mb: 6,

    md: {
        mb: 5
    }
});
